import React, { useCallback, useEffect, useState } from 'react';

import AdminDataTable from '../../components/admin/AdminDataTable';
import { formatAdminDateTime } from '../../components/admin/adminFormatting';
import AdminPagination from '../../components/admin/AdminPagination';
import AuditActionBadge from '../../components/admin/AuditActionBadge';
import { AUDIT_ACTION_CONFIG, formatAuditMetadata } from '../../components/admin/auditFormatting';
import { PageHeader, Select } from '@copilot/ui';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as adminApi from '../../services/adminApi';

const PAGE_SIZE = 20;

const ACTION_FILTERS = [
  { value: '', label: 'All Actions' },
  ...Object.entries(AUDIT_ACTION_CONFIG).map(([value, { label }]) => ({ value, label })),
];

// user/meeting - models/admin_audit_log.py's AdminAuditTarget.
const TARGET_TYPE_FILTERS = [
  { value: '', label: 'All Target Types' },
  { value: 'user', label: 'User' },
  { value: 'meeting', label: 'Meeting' },
];

const EMPTY_FILTERS = {
  action: '',
  adminUserId: '',
  targetType: '',
  targetId: '',
  dateFrom: '',
  dateTo: '',
};

const COLUMNS = [
  { key: 'action', label: 'Action', render: (log) => <AuditActionBadge action={log.action} /> },
  {
    key: 'admin',
    label: 'Admin User',
    render: (log) => (
      <>
        <div>{log.admin.full_name}</div>
        <div className="admin-table-subtext">{log.admin.email}</div>
      </>
    ),
  },
  { key: 'target_type', label: 'Target Type' },
  { key: 'target_id', label: 'Target ID', render: (log) => `#${log.target_id}` },
  { key: 'created_at', label: 'Date', render: (log) => formatAdminDateTime(log.created_at) },
  {
    key: 'metadata',
    label: 'Metadata',
    render: (log) => formatAuditMetadata(log.metadata) || '—',
  },
];

function AdminAuditPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError('');

      try {
        const data = await adminApi.getAdminAuditLogs({
          action: filters.action || undefined,
          admin_user_id: filters.adminUserId || undefined,
          target_type: filters.targetType || undefined,
          target_id: filters.targetId || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          page,
          page_size: PAGE_SIZE,
        });

        if (!cancelled) {
          setLogs(data.items);
          setTotal(data.total);
          setTotalPages(data.total_pages);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.status === 403
              ? 'You do not have permission to view the audit log.'
              : err.message || 'Unable to load audit logs.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  // Silent re-read: no loading/error state, last good data kept on failure.
  const refreshLogs = useCallback(
    async ({ isStale }) => {
      const data = await adminApi.getAdminAuditLogs({
        action: filters.action || undefined,
        admin_user_id: filters.adminUserId || undefined,
        target_type: filters.targetType || undefined,
        target_id: filters.targetId || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        page,
        page_size: PAGE_SIZE,
      });

      if (!isStale()) {
        setLogs(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      }
    },
    [filters, page]
  );

  useAutoRefresh(refreshLogs);

  return (
    <div>
      <PageHeader title="Audit Log" description="Review administrative actions across the platform." />

      <div className="admin-toolbar">
        <div className="admin-toolbar-filter">
          <Select value={filters.action} onChange={setFilter('action')} aria-label="Filter by action">
            {ACTION_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="admin-toolbar-filter">
          <Select value={filters.targetType} onChange={setFilter('targetType')} aria-label="Filter by target type">
            {TARGET_TYPE_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="number"
            min="1"
            placeholder="Admin user ID"
            value={filters.adminUserId}
            onChange={setFilter('adminUserId')}
            aria-label="Filter by admin user ID"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="number"
            min="1"
            placeholder="Target ID"
            value={filters.targetId}
            onChange={setFilter('targetId')}
            aria-label="Filter by target ID"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="date"
            value={filters.dateFrom}
            onChange={setFilter('dateFrom')}
            aria-label="Filter by date from"
          />
        </div>
        <div className="admin-toolbar-filter">
          <input className="input"
            type="date"
            value={filters.dateTo}
            onChange={setFilter('dateTo')}
            aria-label="Filter by date to"
          />
        </div>
      </div>

      {error ? (
        <div className="form-alert">{error}</div>
      ) : (
        <>
          <AdminDataTable
            columns={COLUMNS}
            rows={logs}
            getRowKey={(log) => log.id}
            isLoading={isLoading}
            loadingLabel="Loading audit log..."
            emptyTitle="No activity found."
            emptyDescription="Try a different filter."
          />

          {!isLoading && (
            <AdminPagination
              page={page}
              totalPages={totalPages}
              total={total}
              itemLabel="entries"
              onPageChange={setPage}
              ariaLabel="Audit log pages"
            />
          )}
        </>
      )}
    </div>
  );
}

export default AdminAuditPage;
